import ReactSelect, {
  Props,
  components,
  ClearIndicatorProps,
} from 'react-select'
import { cn } from '../UI/cn'
import React, { forwardRef } from 'react'
import { useModalDepth } from '../UI/Modal/ModalPrimitive'
import { getDropdownZIndex } from '../../styles/z-index'
import { BsXCircleFill } from 'react-icons/bs'

// Custom clear indicator to match FilterCloseButton styling
const CustomClearIndicator = (
  props: ClearIndicatorProps<any, boolean, any>
) => {
  return (
    <components.ClearIndicator {...props}>
      <BsXCircleFill className="w-[13px] h-[13px] text-fg-muted cursor-pointer" />
    </components.ClearIndicator>
  )
}

// ``height`` is a wrapper-only addition that overrides the default
// ``minHeight: 33.5px`` on the control. Other props pass through to
// react-select unchanged.
type WrappedProps = Props & { height?: string }

const ReactSelectWrapper = forwardRef<any, WrappedProps>((props, ref) => {
  // Strip ``height`` from the props that get spread onto react-select
  // — it's a wrapper-only addition and react-select would warn about
  // an unknown HTML prop. Used below to override the control's
  // ``minHeight`` style.
  const { height, ...rest } = props as WrappedProps
  const modalDepth = useModalDepth()
  const zIndex = React.useMemo(
    () => getDropdownZIndex(modalDepth),
    [modalDepth]
  )

  return (
    <ReactSelect
      menuPortalTarget={document.body}
      closeMenuOnScroll={false}
      blurInputOnSelect={!rest.isMulti}
      captureMenuScroll={false}
      menuShouldBlockScroll={false}
      isOptionDisabled={(option: any) => option.disabled}
      components={{
        ClearIndicator: CustomClearIndicator,
        ...rest.components,
      }}
      {...rest}
      styles={{
        ...rest.styles,
        menuPortal: (provided) => ({
          ...provided,
          zIndex,
          pointerEvents: 'all',
        }),
        control: (provided, state) => ({
          // ...provided,
          ...rest.styles?.control?.(provided, state),
          display: 'flex',
          minHeight: height ?? '33.5px',
        }),
        placeholder: (provided, state) => ({
          ...provided,
          ...rest.styles?.placeholder?.(provided, state),
          color: undefined,
        }),
      }}
      classNames={{
        ...rest.classNames,
        control: (provided) => {
          return cn(
            'form-control-select',
            {
              'form-control-select-focus': provided.isFocused,
              'opacity-50': rest.isDisabled,
            },
            rest.classNames?.control?.(provided) ?? ''
          )
        },
        placeholder: (provided) =>
          'form-control-select-placeholder' +
          (rest.classNames?.placeholder?.(provided) ?? ''),
        option: (provided) =>
          `${provided.isFocused ? 'hover-menu-item' : ''} ${
            provided.isSelected ? 'selected-menu-item' : ''
          }` + (rest.classNames?.option?.(provided) ?? ''),
      }}
      ref={ref}
    />
  )
})

ReactSelectWrapper.displayName = 'ReactSelectWrapper'

export default ReactSelectWrapper
